import React, { useState } from 'react'
import { Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { cloneDeep } from 'lodash'
import { Formula } from '../../../../../features/formulas/types/Formula'
import FormulaHelp from '../../../../../features/formulas/components/FormulaHelp'
import FormulaTable from '../../../../../features/formulas/components/FormulaTable'
import FormulaFormModal from '../../../../../features/formulas/components/FormulaFormModal'
import './_Formulas.sass'
import { useCreateFormula } from '../../../../../features/formulas/queries/useCreateFormula'
import { useUpdateFormula } from '../../../../../features/formulas/queries/useUpdateFormula'

const Formulas = () => {
  const { t } = useTranslation()
  const { mutateAsync: createFormula, isPending: createFormulaIsPending } = useCreateFormula()
  const { mutateAsync: updateFormula, isPending: updateFormulaIsPending } = useUpdateFormula()
  const [initialValues, setInitialValues] = useState<Formula>()
  const [visible, setVisible] = useState(false)
  const [formulaError, setFormulaError] = useState<{
    source: string
    error: any
  }>({
    source: '',
    error: null
  })

  const handleEdit = (formula: Formula) => {
    setInitialValues(cloneDeep(formula))
    setVisible(true)
  }

  const handleCancel = () => {
    setInitialValues(undefined)
    setVisible(false)
    setFormulaError({
      source: '',
      error: null
    })
  }

  const handleSubmit = async (values: Omit<Formula, 'style'> & { style: any }) => {
    const {
      id: formulaId,
      style: { fontWeight, ...restStyles },
      ...rest
    } = values

    const requestData = {
      style: {
        fontWeight: fontWeight ? 'bold' : 'normal',
        ...restStyles
      },
      id: formulaId,
      ...rest
    }

    try {
      if (formulaId) {
        await updateFormula(requestData)
      } else {
        await createFormula(requestData)
      }
      handleCancel()
    } catch (e: unknown) {
      console.error('Formula submission error:', e)

      let parsedError: any = 'Unknown error'

      if (e && typeof e === 'object' && 'response' in e) {
        const errorResponse = (e as any).response

        // Try to parse JSON error message
        if (errorResponse?.data?.message) {
          try {
            parsedError = JSON.parse(errorResponse.data.message)
          } catch {
            // If JSON parsing fails, use raw error data
            parsedError = errorResponse.data
          }
        } else if (errorResponse?.data) {
          parsedError = errorResponse.data
        }
      } else if (e instanceof Error) {
        parsedError = e.message
      }

      setFormulaError({
        source: rest.source,
        error: parsedError
      })
      return Promise.reject(Error('Syntax error'))
    }

    return Promise.resolve()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <FormulaHelp />
        <Button
          type="primary"
          onClick={() => {
            setVisible(true)
            setInitialValues(undefined)
          }}
        >
          {`+ ${t('global:add-new')}`}
        </Button>
      </div>
      <FormulaFormModal
        formulaError={formulaError}
        initialValues={initialValues}
        loading={createFormulaIsPending || updateFormulaIsPending}
        visible={visible}
        handleCancel={handleCancel}
        handleSubmit={handleSubmit}
      />
      <FormulaTable handleEdit={handleEdit} />
    </div>
  )
}

export default Formulas
